<script lang="ts">
  import {
    BULK_SALES_CHANNELS,
    BULK_SALES_FLAG_KEYS,
    type BulkSalesFlagKey,
    type BulkSalesFlags,
  } from '@domain/bulk-sales/model';
  import {
    BULK_SALES_CHANNEL_LABELS,
    bulkSalesToggleAllLabel,
    bulkSalesToggleAllPatch,
  } from '@domain/bulk-sales/labels';

  /**
   * 一括営業チャネル除外の編集 UI (spec 20260716「UI Specification」)。
   * 5 チェックボックスを横並び (狭幅で折り返し) に置き、右端に単一の動的テキスト操作
   * (5 つ全 ON のときだけ「すべて解除」、それ以外は「すべて選択」。2026-07-22 決定 6) を
   * 出す。一括操作の状態は保存せず、保存は 5 boolean の PATCH だけ (理由・日付・メモなし)。
   * 法律事務所詳細 (事務所 5 フラグ) と弁護士詳細 (本人 5 フラグ) で共用する。
   */
  interface Props {
    /** 保存値 5 boolean (lawyer / firm のレコードから取り出したもの)。 */
    flags: BulkSalesFlags;
    /** 見出し (例: 営業対象外チャネル / 本人の営業対象外チャネル)。 */
    label: string;
    disabled?: boolean;
    /** PATCH の実行。単一チェックは 1 boolean、一括操作は 5 boolean をまとめて渡す。 */
    onSave: (patch: Partial<BulkSalesFlags>) => Promise<void>;
  }

  let { flags, label, disabled = false, onSave }: Props = $props();

  let saving = $state(false);
  let error = $state<string | null>(null);

  const toggleAllLabel = $derived(bulkSalesToggleAllLabel(flags));

  /// 保存に成功したら true。失敗はインラインのエラー表示にして再操作できるようにする。
  async function commit(patch: Partial<BulkSalesFlags>): Promise<boolean> {
    if (saving) return false;
    saving = true;
    error = null;
    try {
      await onSave(patch);
      return true;
    } catch (err) {
      error = err instanceof Error ? err.message : '保存に失敗しました';
      return false;
    } finally {
      saving = false;
    }
  }

  /// 単一チェック。失敗時は `checked` 属性の値 (保存値) が変わらず DOM だけ操作後の
  /// 状態で残るため、明示的に保存値へ戻す。
  async function commitCheckbox(input: HTMLInputElement, key: BulkSalesFlagKey): Promise<void> {
    const next = input.checked;
    const saved = await commit({ [key]: next });
    if (!saved) input.checked = flags[key];
  }
</script>

<div class="bulk-sales-editor" data-testid="bulk-sales-channel-editor">
  <div class="head">
    <span class="head-label">{label}</span>
    <button
      type="button"
      class="toggle-all"
      data-testid="bulk-sales-toggle-all"
      disabled={disabled || saving}
      onclick={() => void commit(bulkSalesToggleAllPatch(flags))}
    >
      {toggleAllLabel}
    </button>
  </div>
  <div class="channels">
    {#each BULK_SALES_CHANNELS as channel (channel)}
      {@const key = BULK_SALES_FLAG_KEYS[channel]}
      <label class="channel">
        <input
          type="checkbox"
          name={key}
          checked={flags[key]}
          disabled={disabled || saving}
          onchange={(ev) => void commitCheckbox(ev.currentTarget as HTMLInputElement, key)}
        />
        <span>{BULK_SALES_CHANNEL_LABELS[channel]}</span>
      </label>
    {/each}
  </div>
  {#if saving}<span class="muted small">保存中…</span>{/if}
  {#if error}<span class="error-text" role="alert">{error}</span>{/if}
</div>

<style>
  .bulk-sales-editor {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }
  .head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
  }
  .head-label {
    font-weight: 700;
    color: #111827;
  }
  .toggle-all {
    padding: 2px 8px;
    border: 1px solid #d1d5db;
    border-radius: 4px;
    background: #fff;
    color: #2563eb;
    cursor: pointer;
    font: inherit;
    font-size: 0.75rem;
    white-space: nowrap;
  }
  .toggle-all:hover:not(:disabled) {
    background: #eff6ff;
  }
  .toggle-all:disabled {
    cursor: not-allowed;
    opacity: 0.55;
  }
  .channels {
    display: flex;
    flex-wrap: wrap;
    gap: 6px 16px;
  }
  .channel {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    cursor: pointer;
    white-space: nowrap;
  }
  .channel input[type='checkbox'] {
    margin: 0;
  }
  .muted {
    color: #6b7280;
  }
  .small {
    font-size: 0.75rem;
  }
  .error-text {
    color: #b91c1c;
    font-size: 0.75rem;
  }
</style>
